import { useEffect } from 'react';
import { Link } from 'react-router';
import { useSession } from '@hooks/security/use-session';
import { useLocalize } from '@hooks/use-localize';
import { IScreeningResultsModel } from '@/types/screening/screening-results';
import type { ReactNode } from 'react';

interface IProps {
    model: IScreeningResultsModel
}

const ScreeningResults = ({ model }: IProps) => {
    const { registrationId, editKey } = useSession();
    const t = useLocalize(model.languageData);

    useEffect(() => {
        // GTM event fired by the legacy view on render.
        const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({ event: 'screeningResults' });
    }, []);

    const a = model.donorAcceptability;

    const thankYouGenerosity = (
        <p>
            {t(
                'DashLabel_ThankYouForYourGenerosityAndInterest',
                'Thank you for your generosity and interest in giving others the opportunity for a healthier life through donation',
            )}
            .
        </p>
    );

    const renderOutcome = (): ReactNode => {
        if (a.isAcceptable) {
            return (
                <>
                    <h1 style={{ marginLeft: 0 }}>{t('ScreeningAcceptable_Title', "You're almost there")}!</h1>

                    <p>
                        {t(
                            'ScreeningAcceptable_Paragraph_1',
                            'Your donation application has been granted provisional approval. Congratulations, and thank you for your generosity and interest in giving others the opportunity for a healthier life through your donation.',
                        )}
                    </p>

                    <p>
                        {t(
                            'ScreeningAcceptable_Paragraph_2',
                            'The next step is to complete your medical history questionnaire, which should take approximately 45 minutes. To make this process easier, we recommend that you gather your complete medical records before you begin.',
                        )}
                    </p>

                    {a.includeBmiNotice && (
                        <div className="alert alert-warning">
                            {t(
                                'DashLabel_BasedOnYourCalculatedBMI',
                                'Based on your calculated Body Mass Index, you may need to lose some weight before surgery. This will be reviewed with you once you complete the medical history questionnaire',
                            )}
                            .
                        </div>
                    )}

                    <p>
                        <Link className='btn btn-primary' to={"/register/history/demographics/" + registrationId + '/' + editKey}>{t('Button_StartMedicalHistory', 'Start the Medical Questionnaire')}</Link>
                    </p>
                </>
            );
        }

        if (a.isOverseasRuleOut && !model.isDirectedDonor) {
            return (
                <>
                    <h1>{t('DashLabel_ThankYou', 'ThankYou')}!</h1>
                    <p>
                        {t(
                            'DashLabel_BasedOnYourAnswersToOurScreeningSurvey',
                            'Based on your answers to our screening survey for ',
                        )}{' '}
                        {model.siteName},{' '}
                        {t(
                            'DashLabel_YouWouldNotBeAbleToDonateAsAGoodSamaritan',
                            ' you would not be able to donate as a Good Samaritan Donor since you are currently residing outside of the United States. ',
                        )}
                    </p>
                    {thankYouGenerosity}
                </>
            );
        }

        if (a.isOverseasRuleOut && model.isDirectedDonor) {
            return (
                <>
                    <h1>{t('DashLabel_ThankYou', 'ThankYou')}!</h1>
                    <p>
                        {t(
                            'DashLabel_BasedOnYourAnswersToOurScreeningSurvey',
                            'Based on your answers to our screening survey for ',
                        )}{' '}
                        {model.siteName},{' '}
                        {t(
                            'DashLabel_YouWouldNotBeAbleToDonateBecauseFinancial',
                            ' you would not be able to donate because we are not able to provide financial assistance for overseas travel or provide compensation for donations. ',
                        )}
                    </p>
                    {thankYouGenerosity}
                </>
            );
        }

        if (a.hasHardErrors) {
            return (
                <>
                    {a.hardErrors.length > 0 && (
                        <>
                            <h1>{t('DashLabel_ThankYou', 'ThankYou')}!</h1>
                            <p>
                                {t(
                                    'DashLabel_BasedOnYourAnswersToOurScreeningSurvey',
                                    'Based on your answers to our screening survey for ',
                                )}{' '}
                                {model.siteName},{' '}
                                {t(
                                    'DashLabel_ItWouldNotBeMedically',
                                    'you would not be able to donate because of the following:',
                                )}
                            </p>
                            <ul>
                                {a.hardErrors.map((err, i) => (
                                    <li key={i}>{err}</li>
                                ))}
                            </ul>
                        </>
                    )}

                    {a.offerRuleOutTransfer && (
                        <div className="alert alert-success">
                            <h3 style={{ color: 'black' }}>
                                {t('DashLabel_StillInterestedInDonating', 'Still interested in donating?')}
                            </h3>
                            <p>
                                {t(
                                    'DashLabel_RuleOutTransferToNkrOffer',
                                    "Through the National Kidney Registry's Referral Network, we did find at least one center, within 300 miles, that would be willing to evaluate you further. If you are interested in transferring your registration to the National Kidney Registry's Referral Network, you can click on the button below.",
                                )}
                                .
                            </p>
                            <br />
                            <a href="/register/transfer-to-nkr" className="btn btn-primary">
                                {t(
                                    'DashLabel_TransferRegistrationToNKR',
                                    'Transfer Registration to National Kidney Registry',
                                )}
                            </a>
                        </div>
                    )}

                    <p>
                        {t(
                            'DashLabel_OfferRuleOut',
                            'Thank you for your generosity and interest in giving others the opportunity for a healthier life through donation',
                        )}
                        .
                    </p>
                </>
            );
        }

        // Soft-error rule-out.
        return (
            <>
                {a.softErrors.length === 1 && !a.offerProjectDonorWeightLossProgram && (
                    <>
                        <h1>{t('DashLabel_ThankYou', 'ThankYou')}!</h1>
                        <p>
                            {t(
                                'DashLabel_BasedOnYourAnswersToPrelimScreeningNotWorkUpProcess',
                                'Based on your answers to our preliminary screening survey, you are not currently able to start the work-up process to donate because ',
                            )}&nbsp;
                            {a.softErrors[0]}
                        </p>
                    </>
                )}

                {a.softErrors.length >= 2 && (
                    <>
                        <h1>{t('DashLabel_ThankYou', 'ThankYou')}!</h1>
                        <p>
                            {t(
                                'DashLabel_BasedOnYourAnswersToPrelimScreeningNotWorkUpProcess',
                                'Based on your answers to our preliminary screening survey, you are not currently able to start the work-up process to donate because ',
                            )}
                            :
                        </p>
                        <ul>
                            {a.softErrors.map((err, i) => (
                                <li key={i}>{err}</li>
                            ))}
                        </ul>
                    </>
                )}

                {a.offerProjectDonorWeightLossProgram ? (
                    <>
                        <h3 style={{ color: 'black' }}>
                            {t(
                                'DashLabel_CurrentWeightTooHigh',
                                'Unfortunately, your current weight is too high to safely donate.',
                            )}
                        </h3>

                        <h3 style={{ color: 'black' }}>
                            {t('DashLabel_ButWeCanHelp', 'But We Can Help!')}
                        </h3>

                        <p style={{ color: 'black' }}>
                            {t(
                                'DashLabel_ProjectDonorWeCanHelp',
                                "Many individuals ruled out for weight have been able to successfully donate after joining our free weight loss program through Project Donor, which provides free access to weight loss support and resources. If you're interested, click below to continue.",
                            )}
                        </p>

                        <p>
                            <a
                                href="https://www.projectdonor.org/donors-nkr-signupuniquelink?referrer=fbKlIfla3sYQ8S"
                                className="btn btn-primary"
                            >
                                {t('Word_Continue', 'Continue')}
                            </a>
                        </p>
                    </>
                ) : (
                    <p>
                        {t(
                            'DashLabel_PleaseContactUsAgainAtTheAppropriateTime',
                            'Please contact us again at the appropriate time if this changes',
                        )}
                        .
                    </p>
                )}

                {thankYouGenerosity}
            </>
        );
    };

    return renderOutcome();
};

export default ScreeningResults;
